import { PickupDashboardSummaryItem } from '../../../graphql/types/dashboard/PickupDashboardTypes';

export type DrillType = 'reservation' | 'site' | 'none';

export interface MetricRow {
    key: string;
    label: string;
    tooltip?: string;
    numeratorFields: (keyof PickupDashboardSummaryItem)[];
    denominatorFields: (keyof PickupDashboardSummaryItem)[];
    isRate: boolean;
    /** 仅 isRate 时生效：true 表示按 1 - 分子/分母 计算 */
    invertRate?: boolean;
    unit?: 'h' | 'count';
    drillType: DrillType;
    drillFilter?: Record<string, number>;
    children?: MetricRow[];
}

export interface MetricCategory {
    key: string;
    label: string;
    drillType: DrillType;
    children: MetricRow[];
}

export const PICKUP_DASHBOARD_METRICS: MetricCategory[] = [
    {
        key: 'overallResult',
        label: 'dashboard.pickup.metrics.overallResult',
        drillType: 'none',
        children: [
            {
                key: 'collectSuccessRate',
                label: 'dashboard.pickup.metrics.collectSuccessRate',
                tooltip: '截止数据统计日期揽收成功预约单 / 应揽收预约单',
                numeratorFields: ['successCollectReservation'],
                denominatorFields: ['shouldCollectReservation'],
                isRate: true,
                drillType: 'none',
                children: [
                    {
                        key: 'confirmRate_sub',
                        label: 'dashboard.pickup.metrics.confirmRate',
                        tooltip: '已确认预约单 / 应揽收预约单',
                        numeratorFields: ['confirmReservationCount'],
                        denominatorFields: ['dueReservationCount'],
                        isRate: true,
                        drillType: 'none',
                    },
                    {
                        key: 'wiringRate_sub',
                        label: 'dashboard.pickup.metrics.wiringRate',
                        tooltip: '排线预约单（状态含已调度/调度中/揽收中/已揽收/揽收失败） / 已确认预约单',
                        numeratorFields: ['wiringReservationCount'],
                        denominatorFields: ['confirmReservationCount'],
                        isRate: true,
                        drillType: 'none',
                    },
                    {
                        key: 'visitRate_sub',
                        label: 'dashboard.pickup.metrics.visitRate',
                        tooltip: '上门预约单（揽收成功+揽收失败） / 排线预约单',
                        numeratorFields: ['visitReservationCount'],
                        denominatorFields: ['wiringReservationCount'],
                        isRate: true,
                        drillType: 'none',
                    },
                    {
                        key: 'visitSuccessRate_sub',
                        label: 'dashboard.pickup.metrics.visitSuccessRate',
                        tooltip: '揽收成功预约单 / 上门预约单（揽收成功+揽收失败）',
                        numeratorFields: ['successVisitCount'],
                        denominatorFields: ['visitReservationCount'],
                        isRate: true,
                        drillType: 'none',
                    },
                ],
            },
        ],
    },
    {
        key: 'confirmSection',
        label: 'dashboard.pickup.metrics.confirmSection',
        drillType: 'none',
        children: [
            {
                key: 'confirmRate',
                label: 'dashboard.pickup.metrics.confirmRate',
                tooltip: '已确认预约单 / 应揽收预约单',
                numeratorFields: ['confirmReservationCount'],
                denominatorFields: ['dueReservationCount'],
                isRate: true,
                drillType: 'none',
                children: [
                    {
                        key: 'reservationCount',
                        label: 'dashboard.pickup.metrics.reservationCount',
                        tooltip: '该时间周期内产生的全部预约单数量，含已清洗和已取消的',
                        numeratorFields: ['reservationCount'],
                        denominatorFields: [],
                        isRate: false,
                        unit: 'count',
                        drillType: 'none',
                    },
                    {
                        key: 'dueReservationCount',
                        label: 'dashboard.pickup.metrics.dueReservationCount',
                        tooltip: '清洗后地址（剔除已闭环未建联仓）剔除已取消后，该时间周期内产生的预约单',
                        numeratorFields: ['dueReservationCount'],
                        denominatorFields: [],
                        isRate: false,
                        unit: 'count',
                        drillType: 'reservation',
                        drillFilter: { isShouldCollect: 1 },
                    },
                    {
                        key: 'confirmReservationCount',
                        label: 'dashboard.pickup.metrics.confirmReservationCount',
                        tooltip: '该时间周期内，确认的预约单数量',
                        numeratorFields: ['confirmReservationCount'],
                        denominatorFields: [],
                        isRate: false,
                        unit: 'count',
                        drillType: 'none',
                    },
                ],
            },
            {
                key: 'longConfirmRate',
                label: 'dashboard.pickup.metrics.longConfirmRate',
                tooltip: '创建来源为长期揽收的预约单确认率（默认确认）',
                numeratorFields: ['longConfirmCount'],
                denominatorFields: ['longDueCount'],
                isRate: true,
                drillType: 'none',
            },
            {
                key: 'manualConfirmRate',
                label: 'dashboard.pickup.metrics.manualConfirmRate',
                tooltip: '创建来源为手动创建的预约单确认率（默认确认）',
                numeratorFields: ['manualConfirmCount'],
                denominatorFields: ['manualDueCount'],
                isRate: true,
                drillType: 'none',
            },
            {
                key: 'sysConfirmRate',
                label: 'dashboard.pickup.metrics.sysConfirmRate',
                tooltip: '创建来源为系统创建（除长期揽收、手动创建外）的预约单确认率',
                numeratorFields: ['sysConfirmCount'],
                denominatorFields: ['sysDueCount'],
                isRate: true,
                drillType: 'none',
                children: [
                    {
                        key: 'sysNslaConfirmRate',
                        label: 'dashboard.pickup.metrics.sysNslaConfirmRate',
                        tooltip: '系统创建非SLA确认预约单数 / 系统创建非SLA应揽收预约单',
                        numeratorFields: ['sysNslaConfirmCount'],
                        denominatorFields: ['sysNslaDueCount'],
                        isRate: true,
                        drillType: 'none',
                    },
                    {
                        key: 'sysSlaConfirmRate',
                        label: 'dashboard.pickup.metrics.sysSlaConfirmRate',
                        tooltip: '系统创建SLA确认预约单数 / 系统创建SLA应揽收预约单',
                        numeratorFields: ['sysSlaConfirmCount'],
                        denominatorFields: ['sysSlaDueCount'],
                        isRate: true,
                        drillType: 'none',
                    },
                ],
            },
            {
                key: 'temuConfirmRate',
                label: 'dashboard.pickup.metrics.temuConfirmRate',
                tooltip: '下单地址命中TEMU认证仓表的预约单确认率',
                numeratorFields: ['temuConfirmCount'],
                denominatorFields: ['temuDueCount'],
                isRate: true,
                drillType: 'none',
            },
        ],
    },
    {
        key: 'wiringSection',
        label: 'dashboard.pickup.metrics.wiringSection',
        drillType: 'none',
        children: [
            {
                key: 'wiringRate',
                label: 'dashboard.pickup.metrics.wiringRate',
                tooltip: '排线预约单（状态含已调度/调度中/揽收中/已揽收/揽收失败） / 已确认预约单',
                numeratorFields: ['wiringReservationCount'],
                denominatorFields: ['confirmReservationCount'],
                isRate: true,
                drillType: 'none',
            },
            {
                key: 'longWiringRate',
                label: 'dashboard.pickup.metrics.longWiringRate',
                tooltip: '创建来源为长期揽收的预约单排线率',
                numeratorFields: ['longWiringCount'],
                denominatorFields: ['longConfirmCount'],
                isRate: true,
                drillType: 'none',
            },
            {
                key: 'manualWiringRate',
                label: 'dashboard.pickup.metrics.manualWiringRate',
                tooltip: '创建来源为手动创建的预约单排线率',
                numeratorFields: ['manualWiringCount'],
                denominatorFields: ['manualConfirmCount'],
                isRate: true,
                drillType: 'none',
            },
            {
                key: 'sysWiringRate',
                label: 'dashboard.pickup.metrics.sysWiringRate',
                tooltip: '创建来源为系统创建的预约单排线率',
                numeratorFields: ['sysWiringCount'],
                denominatorFields: ['sysConfirmCount'],
                isRate: true,
                drillType: 'none',
                children: [
                    {
                        key: 'sysNslaWiringRate',
                        label: 'dashboard.pickup.metrics.sysNslaWiringRate',
                        tooltip: '系统创建非SLA排线预约单 / 系统创建非SLA确认预约单数',
                        numeratorFields: ['sysNslaWiringCount'],
                        denominatorFields: ['sysNslaConfirmCount'],
                        isRate: true,
                        drillType: 'none',
                    },
                    {
                        key: 'sysSlaWiringRate',
                        label: 'dashboard.pickup.metrics.sysSlaWiringRate',
                        tooltip: '系统创建SLA排线预约单 / 系统创建SLA确认预约单数',
                        numeratorFields: ['sysSlaWiringCount'],
                        denominatorFields: ['sysSlaConfirmCount'],
                        isRate: true,
                        drillType: 'none',
                    },
                ],
            },
            {
                key: 'temuWiringRate',
                label: 'dashboard.pickup.metrics.temuWiringRate',
                tooltip: '下单地址命中TEMU认证仓表的预约单排线率',
                numeratorFields: ['temuWiringCount'],
                denominatorFields: ['temuConfirmCount'],
                isRate: true,
                drillType: 'none',
            },
        ],
    },
    {
        key: 'visitSection',
        label: 'dashboard.pickup.metrics.visitSection',
        drillType: 'none',
        children: [
            {
                key: 'visitRate',
                label: 'dashboard.pickup.metrics.visitRate',
                tooltip: '上门预约单（揽收成功+揽收失败） / 排线预约单',
                numeratorFields: ['visitReservationCount'],
                denominatorFields: ['wiringReservationCount'],
                isRate: true,
                drillType: 'none',
            },
            {
                key: 'visitSuccessRate',
                label: 'dashboard.pickup.metrics.visitSuccessRate',
                tooltip: '揽收成功预约单 / 上门预约单（揽收成功+揽收失败）',
                numeratorFields: ['successVisitCount'],
                denominatorFields: ['visitReservationCount'],
                isRate: true,
                drillType: 'none',
            },
            {
                key: 'longVisitRate',
                label: 'dashboard.pickup.metrics.longVisitRate',
                tooltip: '创建来源为长期揽收的当天上门揽收预约单数 / 创建来源为长期揽收的排线预约单',
                numeratorFields: ['longVisitCount'],
                denominatorFields: ['longWiringCount'],
                isRate: true,
                drillType: 'none',
            },
            {
                key: 'expectedVisitRate',
                label: 'dashboard.pickup.metrics.expectedVisitRate',
                tooltip: '有预计揽收时间的当天上门预约单数 / 有预计揽收时间的排线预约单',
                numeratorFields: ['expectedVisitCount'],
                denominatorFields: ['expectedWiringCount'],
                isRate: true,
                drillType: 'none',
            },
        ],
    },
    // ── 包裹上网 Tab ──────────────────────────────────────────────────────────────
    {
        key: 'packageOnline',
        label: 'dashboard.pickup.metrics.packageOnline',
        drillType: 'none',
        children: [
            {
                key: 't2OnlineRate',
                label: 'dashboard.pickup.metrics.t2OnlineRate',
                tooltip: '有预计提货时间的包裹中 T+2内上网包裹数 / 有预计提货时间的包裹数',
                numeratorFields: ['expectedTimeOnlineQty2'],
                denominatorFields: ['expectedTimePackageQty'],
                isRate: true,
                drillType: 'none',
            },
            {
                key: 'finalOnlineRate',
                label: 'dashboard.pickup.metrics.finalOnlineRate',
                tooltip: '截止数据统计日期上网包裹数 / 下单包裹数',
                numeratorFields: ['onlinePackageQty'],
                denominatorFields: ['orderPackageQty'],
                isRate: true,
                drillType: 'none',
            },
            {
                key: 'avgOnlineDuration',
                label: 'dashboard.pickup.metrics.avgOnlineDuration',
                tooltip: '客户下单至ascan上网的平均时长',
                numeratorFields: ['packageOnlineDuration'],
                denominatorFields: ['ascanOnlinePackageQty'],
                isRate: false,
                unit: 'h',
                drillType: 'none',
            },
        ],
    },
    // ── 新仓建联 Tab ──────────────────────────────────────────────────────────────
    {
        key: 'newSiteConnect',
        label: 'dashboard.pickup.metrics.newSiteConnect',
        drillType: 'none',
        children: [
            {
                key: 'newSiteConnectCompleteRate',
                label: 'dashboard.pickup.metrics.newSiteConnectCompleteRate',
                tooltip: '(新站点建联数 + 新站点确认无法建联数) / 新站点数（剔除重复地址）',
                numeratorFields: ['newWarehouseConnectCount', 'failWarehouseVisitCount'],
                denominatorFields: ['cleanedNewAddressCount'],
                isRate: true,
                drillType: 'none',
                children: [
                    {
                        key: 'newSiteConnectSuccessRate',
                        label: 'dashboard.pickup.metrics.newSiteConnectSuccessRate',
                        tooltip: '新站点建联数 / 新站点数（剔除重复地址）',
                        numeratorFields: ['newWarehouseConnectCount'],
                        denominatorFields: ['cleanedNewAddressCount'],
                        isRate: true,
                        drillType: 'none',
                        children: [
                            {
                                key: 'newSiteConnectSuccessCount',
                                label: 'dashboard.pickup.metrics.newSiteConnectSuccessCount',
                                tooltip: '每天进入审核池且有过处理记录（至少有过初次建联），且未被合并到已有站点或未被AB地址映射，且状态为“建联成功”的地址数',
                                numeratorFields: ['newWarehouseConnectCount'],
                                denominatorFields: [],
                                isRate: false,
                                unit: 'count',
                                drillType: 'none',
                            },
                        ],
                    },
                    {
                        key: 'newSiteConnectFailRate',
                        label: 'dashboard.pickup.metrics.newSiteConnectFailRate',
                        tooltip: '新站点确认无法建联数 / 新站点数（剔除重复地址）',
                        numeratorFields: ['failWarehouseVisitCount'],
                        denominatorFields: ['cleanedNewAddressCount'],
                        isRate: true,
                        drillType: 'none',
                        children: [
                            {
                                key: 'newSiteConnectFailCount',
                                label: 'dashboard.pickup.metrics.newSiteConnectFailCount',
                                tooltip: '每天进入审核池且有过处理记录（至少有过初次建联），且未被合并到已有站点或未被AB地址映射，且状态为“确认无法建联”的地址数',
                                numeratorFields: ['failWarehouseVisitCount'],
                                denominatorFields: [],
                                isRate: false,
                                unit: 'count',
                                drillType: 'none',
                            },
                        ],
                    },
                    {
                        key: 'newSiteCount_complete',
                        label: 'dashboard.pickup.metrics.newSiteCount',
                        tooltip: '每天进入审核池且有过处理记录（至少有过初次建联），且未被合并到已有站点或未被AB地址映射的地址数',
                        numeratorFields: ['cleanedNewAddressCount'],
                        denominatorFields: [],
                        isRate: false,
                        unit: 'count',
                        drillType: 'site',
                        drillFilter: { isCleaned: 1 },
                    },
                ],
            },
            {
                key: 'duplicateAddressRate',
                label: 'dashboard.pickup.metrics.duplicateAddressRate',
                tooltip: '1 - 新站点数（剔除重复地址） / 审核池已处理地址数',
                numeratorFields: ['cleanedNewAddressCount'],
                denominatorFields: ['newAddressProcessCount'],
                isRate: true,
                invertRate: true,
                drillType: 'none',
                children: [
                    {
                        key: 'newSiteCount_duplicate',
                        label: 'dashboard.pickup.metrics.newSiteCount',
                        tooltip: '每天进入审核池且有过处理记录（至少有过初次建联），且未被合并到已有站点或未被AB地址映射的地址数',
                        numeratorFields: ['cleanedNewAddressCount'],
                        denominatorFields: [],
                        isRate: false,
                        unit: 'count',
                        drillType: 'site',
                        drillFilter: { isCleaned: 1 },
                    },
                    {
                        key: 'auditPoolProcessedCount_duplicate',
                        label: 'dashboard.pickup.metrics.auditPoolProcessedCount',
                        tooltip: '每天进入审核池且有过处理记录（至少有过初次建联）的地址数',
                        numeratorFields: ['newAddressProcessCount'],
                        denominatorFields: [],
                        isRate: false,
                        unit: 'count',
                        drillType: 'none',
                    },
                ],
            },
            {
                key: 'auditPoolPendingRate',
                label: 'dashboard.pickup.metrics.auditPoolPendingRate',
                tooltip: '1 - 审核池已处理地址数 / 审核池地址数',
                numeratorFields: ['newAddressProcessCount'],
                denominatorFields: ['newAddressCount'],
                isRate: true,
                invertRate: true,
                drillType: 'none',
                children: [
                    {
                        key: 'auditPoolProcessedCount_pending',
                        label: 'dashboard.pickup.metrics.auditPoolProcessedCount',
                        tooltip: '每天进入审核池且有过处理记录（至少有过初次建联）的地址数',
                        numeratorFields: ['newAddressProcessCount'],
                        denominatorFields: [],
                        isRate: false,
                        unit: 'count',
                        drillType: 'none',
                    },
                    {
                        key: 'auditPoolAddressCount',
                        label: 'dashboard.pickup.metrics.auditPoolAddressCount',
                        tooltip: '每天进入审核池的地址数',
                        numeratorFields: ['newAddressCount'],
                        denominatorFields: [],
                        isRate: false,
                        unit: 'count',
                        drillType: 'none',
                    },
                ],
            },
        ],
    },
];
